import React, { useState, useEffect } from 'react';
import { 
  GraduationCap, 
  Save, 
  Upload, 
  Clock, 
  Image as ImageIcon, 
  Eye, 
  X, 
  Edit3 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function EducationProgramHeroForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    summary: '',
    slides: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    summary: false
  });

  const [pendingSlides, setPendingSlides] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [editingSlideIndex, setEditingSlideIndex] = useState(null);
  const [modalSlideData, setModalSlideData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/education/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[EducationProgramHeroForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let heroSection = null;
      
      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          heroSection = result.data.find(s => s.section_type === 'education-program-hero');
        }
      }
      
      if (!heroSection) {
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            heroSection = result.data.find(s => s.section_type === 'education-program-hero');
          }
        }
      }
      
      if (!heroSection) {
        throw new Error('Education Program Hero section not found');
      }

      const currentSectionId = heroSection.id;
      setSectionId(currentSectionId);

      let published = heroSection.content_published;
      if (typeof published === 'string') {
        try { published = JSON.parse(published); } catch (e) { published = {}; }
      }

      const publishedEn = published?.en || {};
      const initialData = {
        kicker: publishedEn.kicker || 'Education Program',
        title: publishedEn.title || 'Transformative Academic Pathways',
        summary: publishedEn.summary || 'Empowering young minds through world-class bilingual curricula and holistic character formation.',
        slides: Array.isArray(publishedEn.slides) ? publishedEn.slides.slice(0, 3) : []
      };

      const pendingFieldsMap = { kicker: false, title: false, summary: false };
      const pendingSlidesMap = {};

      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${currentSectionId}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
          const { pending_changes } = pendingResult.data;

          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                let val = change.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (change.field_id === 'kicker' || change.field_id === 'title' || change.field_id === 'summary') {
                  pendingFieldsMap[change.field_id] = true;
                  if (val !== undefined && val !== null) {
                    initialData[change.field_id] = val;
                  }
                } else if (change.field_id && change.field_id.includes('slides.')) {
                  const match = change.field_id.match(/slides\.(\d+)\./);
                  if (match) {
                    const slideIdx = parseInt(match[1], 10);
                    pendingSlidesMap[`slide_${slideIdx}`] = true;
                    if (val && initialData.slides[slideIdx]) {
                      initialData.slides[slideIdx] = val;
                    }
                  }
                }
              }
            });
          }
        }
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingFieldsMap);
      setPendingSlides(pendingSlidesMap);
    } catch (err) {
      console.error('[EducationProgramHeroForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };



  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                setModalSlideData(prev => ({ ...prev, image: mediaId }));
                toast.success('Image uploaded successfully');
              } else {
                toast.error('No media ID in response');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/education/cms/media/upload?section_type=education-program-hero`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const fieldNames = {
        kicker: 'Education Hero Tag / Kicker',
        title: 'Education Hero Title',
        summary: 'Education Hero Summary'
      };

      const requestBody = {
        section_id: sectionId,
        field_id: fieldName,
        field_name: fieldNames[fieldName] || fieldName.replace(/_/g, ' '),
        new_value: formData[fieldName],
        old_value: originalData[fieldName],
        locale: 'en'
      };

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Change submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleCloseSlideModal = () => {
    if (editingSlideIndex !== null && editingSlideIndex >= originalData.slides.length) {
      setFormData(prev => ({
        ...prev,
        slides: prev.slides.filter((_, i) => i !== editingSlideIndex)
      }));
    }
    setEditingSlideIndex(null);
    setModalSlideData(null);
  };

  const handleSubmitSlideModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (!modalSlideData || !modalSlideData.image) {
      toast.warning('Please upload an image for this slide');
      return;
    }

    const slideIndex = editingSlideIndex;
    const slideKey = `slide_${slideIndex}`;
    setSubmitting(prev => ({ ...prev, [slideKey]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const isNew = slideIndex >= originalData.slides.length;
      const op = isNew ? 'add' : 'update';

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `slides.${slideIndex}.${op}`,
            field_name: `${isNew ? 'Add' : 'Update'} Education Slide ${slideIndex + 1}`,
            old_value: isNew ? null : (originalData.slides[slideIndex] || {}),
            new_value: modalSlideData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Slide ${slideIndex + 1} submitted for approval`);

        setFormData(prev => ({
          ...prev,
          slides: prev.slides.map((s, i) => i === slideIndex ? modalSlideData : s)
        }));

        setOriginalData(prev => {
          const nextSlides = [...prev.slides];
          nextSlides[slideIndex] = modalSlideData;
          return { ...prev, slides: nextSlides };
        });

        setPendingSlides(prev => ({
          ...prev,
          [slideKey]: true
        }));

        setEditingSlideIndex(null);
        setModalSlideData(null);
      } else {
        if (response.status === 409) {
          toast.error(`Slide ${slideIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [slideKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Education Hero Section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <GraduationCap size={22} />
          </div>
          <div>
            <h2 className="unified-title">Education Program - Hero Banner</h2>
            <p className="unified-subtitle">Manage the education portal opening headline, curriculum summary, and carousel slides</p>
          </div>
        </div>
      </div>

      {/* Field 0: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Education Hero Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="60"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. Education Program"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Field 1: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Education Hero Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="100"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. Transformative Academic Pathways"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Field 2: Summary */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Education Hero Summary</label>
          {pendingFields.summary && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength="500"
            className="unified-textarea"
            rows="3"
            value={formData.summary}
            onChange={(e) => handleFieldChange('summary', e.target.value)}
            disabled={pendingFields.summary || submitting.summary}
            placeholder="Empowering young minds through world-class bilingual curricula and holistic character formation..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('summary')}
            disabled={submitting.summary || pendingFields.summary || formData.summary === originalData.summary}
          >
            <Save size={14} /> {submitting.summary ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Field 3: Carousel Slides (Vertical downward stack) */}
      <div style={{ marginTop: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
              Hero Carousel Slides (3 Slides)
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
              Full-width background slides rotating in the Education Program hero section (Replace Image Only)
            </p>
          </div>
        </div>

        {formData.slides.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '14px', border: '2px dashed #cbd5e1' }}>
            <ImageIcon size={38} color="#94a3b8" style={{ margin: '0 auto 12px' }} />
            <h4 style={{ fontSize: '15px', color: '#334155', margin: '0 0 4px', fontWeight: '700' }}>No Carousel Slides Added</h4>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Click "Add Slide" to upload banner photos</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginTop: '12px' }}>
            {formData.slides.map((slide, idx) => {
              const isPending = pendingSlides[`slide_${idx}`];
              const imgSrc = getMediaUrl(slide.image);

              return (
                <div
                  key={idx}
                  style={{
                    border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                    borderRadius: '14px',
                    background: isPending ? '#fffdf7' : '#ffffff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {/* Thumbnail */}
                  <div
                    style={{
                      height: '150px',
                      background: '#0f172a',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    {imgSrc ? (
                      <>
                        <img
                          src={imgSrc}
                          alt={`Slide ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => setLightboxImage({ src: imgSrc, title: `Slide ${idx + 1}` })}
                        />
                        <button
                          type="button"
                          onClick={() => setLightboxImage({ src: imgSrc, title: `Slide ${idx + 1}` })}
                          style={{
                            position: 'absolute',
                            bottom: '8px',
                            right: '8px',
                            background: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '11px',
                            gap: '4px'
                          }}
                          title="Zoom Image"
                        >
                          <Eye size={12} />
                        </button>
                      </>
                    ) : (
                      <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                        <ImageIcon size={28} />
                        <span style={{ fontSize: '12px' }}>Belum ada foto</span>
                      </div>
                    )}
                  </div>

                  {/* Slide Info & Actions */}
                  <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                        Slide {idx + 1}
                      </span>
                      {isPending && (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                      <button
                        type="button"
                        className="unified-secondary-btn"
                        onClick={() => {
                          if (isPending) {
                            toast.info('This slide is awaiting approval');
                            return;
                          }
                          setEditingSlideIndex(idx);
                          setModalSlideData({ ...slide });
                        }}
                        disabled={isPending}
                        style={{ flex: 1, justifyContent: 'center' }}
                      >
                        <Edit3 size={13} /> {slide.image ? 'Change Photo' : 'Upload Photo'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Slide Modal */}
      {editingSlideIndex !== null && modalSlideData && (
        <div className="unified-modal-overlay" onClick={handleCloseSlideModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                  Edit Slide {editingSlideIndex + 1} Photo
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Upload high-resolution photography for this education hero banner
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseSlideModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              {/* Image Preview Box */}
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                {modalSlideData.image && getMediaUrl(modalSlideData.image) ? (
                  <div style={{ width: '100%', height: '220px', borderRadius: '12px', overflow: 'hidden', background: '#0f172a', border: '1px solid #cbd5e1' }}>
                    <img
                      src={getMediaUrl(modalSlideData.image)}
                      alt="Slide preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ) : (
                  <div style={{ width: '100%', height: '180px', borderRadius: '12px', border: '2px dashed #cbd5e1', background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#94a3b8' }}>
                    <ImageIcon size={36} />
                    <span style={{ fontSize: '13px', fontWeight: '500' }}>No slide image selected yet</span>
                  </div>
                )}
              </div>

              {/* Upload Input Area */}
              <div style={{ background: '#f1f5f9', padding: '16px 20px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '2px' }}>
                    Select Image File
                  </label>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    JPG, PNG, WebP • Max 5MB • Recommended: 1920×1080px
                  </span>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type="file"
                    id="education-slide-modal-file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                  />
                  <label
                    htmlFor="education-slide-modal-file"
                    className="unified-secondary-btn"
                    style={{ cursor: 'pointer', background: '#ffffff' }}
                  >
                    <Upload size={14} /> {imageUploading ? `Uploading ${imageProgress}%...` : 'Browse & Upload'}
                  </label>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseSlideModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitSlideModal}
                disabled={submitting[`slide_${editingSlideIndex}`] || !modalSlideData.image || imageUploading}
              >
                <Save size={14} /> {submitting[`slide_${editingSlideIndex}`] ? 'Submitting...' : 'Save & Submit Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title || 'Slide photo'}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
